import { Component, DestroyRef, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DATE_FORMATS, MatOption } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSelect } from '@angular/material/select';
import { Router } from '@angular/router';
import { AccessOptions } from '@shared/models/accessOptions.model';
import { AllFormation } from '@shared/models/allFormation.model';
import { Formation } from '@shared/models/formation.model';
import { FormationsGroups } from '@shared/models/formationsGroups.model';
import { InstanceIdService } from '@shared/services/instance-id.service';
import { ParamedicalService } from '@shared/services/paramedical.service';
import { SubscriptionManager } from '@shared/services/subscription-manager.service';
import { firstValueFrom } from 'rxjs';
import { StorageService } from 'src/app/core/services/storage.service';
import { Iade, NURSE, OPTIONS } from '../../const/globals.const';
import { Profile } from '../../models/profile.model';
import { User } from '../../models/user.model';
import { ErrorService } from '../../services/error.service';
import { HospitalService } from '../../services/hospital.service';
import { MatchingresultsService } from '../../services/matchingresults.service';
import { ToastService } from '../../services/toast.service';
import { UserService } from '../../services/user.service';
import { transformedDate } from '../../utils/cross-functions';
import { CropperPopupComponent } from '../edit-personal-data/cropper-popup/cropper-popup.component';
import { ImportProfileDialogComponent } from '../import-profile-dialog/import-profile-dialog.component';
import { MY_FORMATS } from './data-format.const';
import { posthog } from 'posthog-js';

const FILE_SIZE_MAX = 2400000; //24mo
const ALLOWED_EXTENSIONS = ['png', 'jpeg', 'jpg'];
const ERROR_MSG_ON_FILE_SIZE = "La taille de l'image dépasse la capacité de 2,5 mo autorisée";
const ERROR_MSG_ON_EXTENSION =
  "Ce type d'image n'est pas autorisé. Veuillez choisir une image dans les formats suivants :JPG, PNG,JPEG)";

@Component({
  selector: 'app-add-profile',
  templateUrl: './add-profile.component.html',
  styleUrls: ['./add-profile.component.scss'],
  providers: [{ provide: MAT_DATE_FORMATS, useValue: MY_FORMATS }],
  standalone: false,
})
export class AddProfileComponent implements OnInit {
  private instanceId: string;

  @Input() public topAccessOptions: AccessOptions | undefined;

  @Input() public type: string = '';

  @Input() public formationsGroup: FormationsGroups | undefined;

  @Input() public openType: string = '';

  @Input() public profile?: Profile;

  @Input() public activeSkills: boolean = false;

  @Output() public onProfileChange = new EventEmitter<{ profile: Profile; showSkillsPopup: boolean }>();

  @Output() public onClose = new EventEmitter();

  @Output() public editSkills = new EventEmitter();

  @ViewChild('search', { static: false }) public searchElement: ElementRef | undefined;

  @ViewChild('matRef', { static: false }) public matRef: MatSelect | undefined;

  public INFIRMIER: string = NURSE;

  public formGroup: FormGroup | undefined;

  public currentUser: User | undefined;

  public imageSource: string = '';

  public showSeniority: boolean = false;

  public realFirstName: string = '';

  public postes: string[] = [];

  public postesAllData: any[] = [];

  public picture: string = '';

  public residencies: { label: string; value: string }[] = [];

  public residencyCadreBloc: { label: string; value: string }[] = [
    {
      label: 'Titulaire',
      value: 'Titulaire',
    },
  ];

  public seniorities: any[] = [];

  public _isExtensionAllowed: boolean = false;

  public _isFileSizeAllowed: boolean = false;

  public isMultiHospitals: boolean = false;

  public hospitals: any[] = [];

  public isImportedProfile: boolean = false;

  public importedProfile: Profile | undefined;

  public matchingResults: any[] = [];

  public options: any = null;

  public selectedOptions: any[] = [];

  public selectFormControl = new FormControl();

  public isLoading: boolean = true;

  public accessOptions: AccessOptions | undefined;

  public formationsGroups: FormationsGroups[] = [];

  public showMatchingResults: boolean = false;

  public softwareName: string | null = null;

  public allFormations: AllFormation[] = [];

  public formationsThatHaveSkills: Formation[] = [];

  public doesSelectedFormationHaveSkills: boolean = false;

  public doesHospitalHaveCalendarsDataPipelineOption: boolean = false;

  public excludeInCalendarsConnector: boolean = false;

  public calendarsSoftwareName: string | null = null;

  public get isNurse() {
    if (this.formGroup && this.postes.length > 0) {
      const selectedName = this.formGroup.get('type')?.value;
      const selectedPoste = this.postesAllData.find((poste: any) => poste.name === selectedName);
      if (!selectedPoste) {
        return false;
      }

      const selectedPosteAllFormations = this.allFormations.find(
        (formation: any) => formation.name === selectedPoste.name
      );
      if (!selectedPosteAllFormations) {
        return false;
      }

      const nursesGroup = this.formationsGroups?.find((group: any) => group.name === 'NURSES');
      if (!nursesGroup || !nursesGroup.formations) {
        return false;
      }

      return nursesGroup.formations.some((id: any) => id.toString() === selectedPosteAllFormations._id.toString());
    }
    return false;
  }

  constructor(
    private hospitalService: HospitalService,
    private formBuilder: FormBuilder,
    private toastService: ToastService,
    private storageService: StorageService,
    private userService: UserService,
    public dialog: MatDialog,
    private readonly matchingResultsService: MatchingresultsService,
    private readonly errorService: ErrorService,
    private router: Router,
    private paramedicalService: ParamedicalService,
    private destroyRef: DestroyRef,
    private subscriptionManager: SubscriptionManager,
    private instanceIdService: InstanceIdService
  ) {
    this.instanceId = this.instanceIdService.generateId();
    this.subscriptionManager.registerComponent(this.instanceId, this.destroyRef);
  }

  async ngOnInit() {
    this.allFormations = await this.userService.getAllFormations();
    await this.initFormationsThatHaveSkills();
    if (!this.topAccessOptions) {
      this.accessOptions = this.userService.getAccessOptionsOfPage(this.router.url);
    } else {
      this.accessOptions = this.topAccessOptions;
    }
    this.isMultiHospitals = this.userService.isCurrentUserHasMultipleHospitalsSelected();
    this.currentUser = this.storageService.getUser();
    this.formationsGroups = await this.userService.getAllFormationsGroups();
    if (this.isMultiHospitals) {
      const selectedHospitalsId = this.userService.getSelectedHospitals();
      const principalHospitalId = this.userService.getUserPrincipalHospitalId();
      selectedHospitalsId.forEach((hospitalId: any) => {
        const hospital = this.currentUser?.profile?.hospitals?.find(
          (hospital: any) => String(hospital._id) == String(hospitalId)
        );
        if (hospitalId === principalHospitalId) {
          this.hospitals.unshift({
            name: hospital?.name,
            _id: hospital?._id,
            displayName: this.userService.resolveHospitalName(String(hospital?._id)),
          });
        } else {
          this.hospitals.push({
            name: hospital?.name,
            _id: hospital?._id,
            displayName: this.userService.resolveHospitalName(String(hospital?._id)),
          });
        }
      });
    }
    this.showSeniority = false;
    this.residencies =
      (this.openType === 'EDIT'
        ? ['Anesthésiste', 'Iade'].includes(this.profile?.position ?? '')
          ? this.accessOptions?.residencyInEditPopupAnesth
          : this.accessOptions?.residencyInEditPopupNurse
        : this.accessOptions?.residencyInPopUp
      )?.map((residency: string) => ({
        label: residency,
        value: residency,
      })) ?? [];

    this.postesAllData = await this.userService.getAccessOptionsPositionsByPositions(
      this.openType === 'EDIT'
        ? ['Anesthésiste', 'Iade'].includes(this.profile?.position ?? '')
          ? this.accessOptions?.positionsInEditPopupAnesth
          : this.accessOptions?.positionsInEditPopupNurse
        : (this.accessOptions?.positionsInPopUp ?? []),
      true
    );

    await this.setPostes();

    await this.continueInit();
    this.isLoading = false;
  }

  async setPostes(): Promise<void> {
    // If the user has multiple hospitals selected, we will filter the formations based on the selected hospital.
    // If not, we don't filter on hospital.

    // If the user has multiple hospitals selected and the form is not already created, we will use the first hospital as default.
    const selectedHospitalId = this.isMultiHospitals
      ? (this.formGroup?.get('hospitals')?.value._id ?? this.hospitals[0]._id)
      : undefined;

    this.postes = (
      this.formationsGroup
        ? await this.userService.getFormationsByGroup(this.postesAllData, this.formationsGroup)
        : await this.userService.getAccessOptionsPositionsByPositions(this.accessOptions?.positionsInPopUp, true)
    )
      .filter((formation: Formation) => (this.isMultiHospitals ? formation.hospital === selectedHospitalId : true))
      .map((formation: Formation) => formation.name ?? '');

    // If we have the selected position does not exist on the positions list which means that the user has changed the selected hospital, we will set the first position as default.
    if (this.formGroup?.get('type')?.value && !this.postes.includes(this.formGroup?.get('type')?.value)) {
      this.formGroup?.get('type')?.setValue(this.postes[0]);
    } else {
      this.setDoesSelectedFormationHaveSkills();
    }
  }

  listenToHospitalChanges(): void {
    if (this.formGroup?.get('hospitals')) {
      this.subscriptionManager
        .createRequest(this.formGroup.get('hospitals')!.valueChanges, 'hospitalsValueChanges', this.instanceId)
        .subscribe({
          next: (_data: any) => {
            this.setPostes();
          },
          error: (error: Error) => {
            console.error(error);
          },
        });
    }
  }

  setDoesSelectedFormationHaveSkills(): void {
    const selectedHospitalId = this.isMultiHospitals ? this.formGroup?.get('hospitals')?.value._id : undefined;

    this.doesSelectedFormationHaveSkills = this.formationsThatHaveSkills.some((formation: Formation) => {
      return (
        formation.name === this.formGroup?.get('type')?.value &&
        (this.isMultiHospitals ? formation.hospital === selectedHospitalId : true)
      );
    });
  }

  async continueInit() {
    await this.createForm();
    this.listenToHospitalChanges();
    this.getMatchingResultsByProfile();
    this.setDoesSelectedFormationHaveSkills();
    this.initCalendarsDataPipelineOption();

    if (this.profile && this.profile.position && this.profile.seniority && this.profile.seniority.length > 0) {
      this.formGroup?.get('seniority')?.setValue(this.profile?.seniority);
    }

    if (this.formGroup?.get('type')) {
      this.subscriptionManager
        .createRequest(this.formGroup.get('type')!.valueChanges, 'typeValueChanges', this.instanceId)
        .subscribe({
          next: async (val: any) => {
            const formationsGroupOfType = await this.userService.getFormationsGroupOfProfile({ position: val });
            this.showSeniority = formationsGroupOfType?.isUsingSeniorities ?? false;
            if (this.showSeniority) {
              if (this.accessOptions?.seniority?.length || this.accessOptions?.seniorityInEditPopupAnesth?.length) {
                this.seniorities =
                  (this.openType === 'EDIT'
                    ? this.accessOptions?.seniorityInEditPopupAnesth
                    : this.accessOptions.seniority
                  )?.map((seniority: string) => ({
                    label: seniority,
                    value: seniority,
                  })) ?? [];
              }
              this.formGroup?.get('seniority')?.setValue(this.seniorities[0].value);
            }

            this.setDoesSelectedFormationHaveSkills();
            await this.updateShowMatchingResults();
            await this.updateSoftwareName();
          },

          error: (error: any) => {
            this.errorService.handleError(error);
          },
        });
    }
  }

  getMatchingResultsByProfile() {
    this.subscriptionManager
      .createRequest(
        this.matchingResultsService.searchMatchingResults('profile', { doPagination: false }),
        'searchMatchingResults',
        this.instanceId
      )
      .subscribe({
        next: async (res: any) => {
          this.matchingResults = res.docs;

          let rawOptions: any[] = [];

          if (this.isMultiHospitals) {
            const selectedHospitalId = this.formGroup?.get('hospitals')?.value._id;
            rawOptions = this.matchingResults.filter((matchingResult: any) => {
              return String(matchingResult.hospital) === String(selectedHospitalId);
            });
          } else {
            rawOptions = res.docs;
          }

          this.options = this.enrichOptionsWithSoftwareLabel(rawOptions);

          if (this.selectFormControl) {
            if (!this.options || !this.options.length) {
              this.selectFormControl.disable();
            } else {
              this.selectFormControl.enable();
            }
          }

          if ('EDIT' === this.openType) {
            this.selectedOptions = this.options.filter((opt: any) => opt.matchedId === this.profile?._id);
            this.sortOptions();
            this.matRefSelect();
          }

          await this.updateShowMatchingResults();
          await this.updateSoftwareName();
        },
        error: (error: any) => this.errorService.handleError(error),
      });
  }

  reverse(s: string): string {
    return s.split('-').reverse().join('-');
  }

  async createForm() {
    const profile: Profile = this.profile ? this.profile : {};
    let staDate: Date | undefined;
    if (this.profile) {
      const reversedDate = this.profile.startingDate;
      staDate = new Date(reversedDate);
    }
    if (profile.profilePic && profile.profilePic !== '') {
      this.picture = profile.profilePic;
      this.imageSource = this.picture;
    }
    const type = this.getType(profile);

    const formationsGroupOfType = await this.userService.getFormationsGroupOfProfile({ position: type });
    this.showSeniority = formationsGroupOfType?.isUsingSeniorities ?? false;

    if (this.showSeniority) {
      if (this.accessOptions?.seniority?.length || this.accessOptions?.seniorityInEditPopupAnesth?.length) {
        this.seniorities =
          (this.openType === 'EDIT'
            ? this.accessOptions?.seniorityInEditPopupAnesth
            : this.accessOptions.seniority
          )?.map((seniority: string) => ({
            label: seniority,
            value: seniority,
          })) ?? [];
      }
    }
    const excludeCtrl = {
      value: profile.excludeInCalendarsConnector ?? false,
      disabled: this.isImportedProfile,
    };

    if (!this.isMultiHospitals) {
      this.formGroup = this.formBuilder.group({
        firstName: [
          { value: profile.firstName, disabled: this.isImportedProfile },
          [Validators.required, Validators.minLength(2)],
        ],
        lastName: [
          { value: profile.lastName, disabled: this.isImportedProfile },
          [Validators.required, Validators.minLength(2)],
        ],
        phoneNumber: [{ value: profile.phoneNumber, disabled: this.isImportedProfile }],
        seniority: [
          {
            value: this.accessOptions?.seniority ? this.accessOptions?.seniority?.[0] : profile.seniority,
            disabled: this.isImportedProfile,
          },
        ],
        type: [{ value: type, disabled: this.isImportedProfile }, [Validators.required]],
        residency: [{ value: profile.residency, disabled: this.isImportedProfile }, [Validators.required]],
        startingDate: [{ value: staDate, disabled: this.isImportedProfile }],
        // principalHospitalId: [this.hospitals[0]._id, [Validators.required]]
        excludeInCalendarsConnector: [excludeCtrl],
      });
    } else {
      this.formGroup = this.formBuilder.group({
        firstName: [
          { value: profile.firstName, disabled: this.isImportedProfile },
          [Validators.required, Validators.minLength(2)],
        ],
        lastName: [
          { value: profile.lastName, disabled: this.isImportedProfile },
          [Validators.required, Validators.minLength(2)],
        ],
        phoneNumber: [{ value: profile.phoneNumber, disabled: this.isImportedProfile }],
        seniority: [
          {
            value: this.accessOptions?.seniority ? this.accessOptions?.seniority?.[0] : profile.seniority,
            disabled: this.isImportedProfile,
          },
        ],
        type: [{ value: type, disabled: this.isImportedProfile }, [Validators.required]],
        residency: [{ value: profile.residency, disabled: this.isImportedProfile }, [Validators.required]],
        startingDate: [{ value: staDate, disabled: this.isImportedProfile }],
        hospitals: [this.hospitals[0], [Validators.required]],
        // principalHospitalId: [this.hospitals[0]._id, [Validators.required]]
        excludeInCalendarsConnector: [excludeCtrl],
      });
    }
  }

  getType(profile: any) {
    if (profile.position) {
      if (profile.position === 'IADE') {
        return Iade;
      }
      return profile.position;
    } else {
      if (this.postes.length > 0) {
        return this.postes[0];
      }
      return '';
    }
  }

  resetHospitals() {
    this.hospitals = [];
    const selectedHospitalsId = this.userService.getSelectedHospitals();
    const principalHospitalId = this.userService.getUserPrincipalHospitalId();
    selectedHospitalsId.forEach((hospitalId: any) => {
      const hospital = this.currentUser?.profile?.hospitals?.find(
        (hospital: any) => String(hospital._id) == String(hospitalId)
      );
      if (hospitalId === principalHospitalId) {
        this.hospitals.unshift({
          name: hospital?.name,
          _id: hospital?._id,
          displayName: this.userService.resolveHospitalName(String(hospital?._id)),
        });
      } else {
        this.hospitals.push({
          name: hospital?.name,
          _id: hospital?._id,
          displayName: this.userService.resolveHospitalName(String(hospital?._id)),
        });
      }
    });
  }

  importProfile() {
    const dialogRef = this.dialog.open(ImportProfileDialogComponent, {
      width: '320px',
      height: '416px',
      data: {
        profileType: this.type,
      },
    });

    this.subscriptionManager.createRequest(dialogRef.afterClosed(), 'ImportProfileDialog', this.instanceId).subscribe({
      next: (res: any) => {
        if (res && res.status) {
          this.resetHospitals();
          res.profile.hospitals.forEach((hospital: any) => {
            this.hospitals = this.hospitals.filter((hospital_: any) => String(hospital_._id) !== String(hospital._id));
          });
          this.formGroup?.patchValue({
            firstName: res.profile.firstName,
            lastName: res.profile.lastName,
            phoneNumber: res.profile.phoneNumber,
            seniority: res.profile.seniority ? res.profile.seniority : '',
            type: res.profile.position,
            residency: res.profile.residency,
            startingDate: res.profile.startingDate,
            hospitals: this.hospitals[0],
            principalHospitalId: this.hospitals[0],
          });
          this.isImportedProfile = true;
          this.importedProfile = res.profile;
          if (!this.importedProfile!.profilePic) {
            this.importedProfile!.profilePic = 'assets/images/icons/nurse.svg';
          }
          if (this.hospitals[0]) {
            const rawOptions = this.matchingResults.filter((matchingResult: any) => {
              return String(matchingResult.hospital) === String(this.hospitals[0]._id);
            });

            this.options = this.enrichOptionsWithSoftwareLabel(rawOptions);

            if (this.selectFormControl) {
              if (!this.options || !this.options.length) {
                this.selectFormControl.disable();
              } else {
                this.selectFormControl.enable();
              }
            }
          }
        }
      },

      error: (error: any) => {
        this.errorService.handleError(error);
      },
    });
  }

  onSelectedHospitalChange() {
    this.selectedOptions = [];
    this.selectFormControl.patchValue(null);
    this.matRef?.options.forEach((option: MatOption) => option.deselect());

    const selectedHospitalId = this.formGroup?.get('hospitals')?.value._id;

    const rawOptions = this.matchingResults.filter((matchingResult: any) => {
      return String(matchingResult.hospital) === String(selectedHospitalId);
    });

    this.options = this.enrichOptionsWithSoftwareLabel(rawOptions);

    if (this.selectFormControl) {
      if (!this.options || !this.options.length) {
        this.selectFormControl.disable();
      } else {
        this.selectFormControl.enable();
      }
    }

    this.sortOptions();
    this.matRefSelect();
  }

  async submitProfile() {
    if (this.formGroup?.valid) {
      let hospital;
      const profile: any = this.profile ? this.profile : {};
      if (this.isImportedProfile) {
        profile._id = this.importedProfile?._id;
      }
      if (this.openType === 'ADD') {
        posthog.capture('Create_Profile', { page_source: 'Profile_List' });
        if (this.isMultiHospitals) {
          const selectedHospital = this.formGroup?.get('hospitals')?.value;
          const userHospitals = this.currentUser?.profile?.hospitals;
          hospital = userHospitals?.find(
            (userHospital: any) => String(userHospital._id) === String(selectedHospital._id)
          );
          profile.hospitals = [hospital?._id];
          profile.principalHospitalId = hospital?._id;
        } else {
          const selectedHospital = this.userService.getSelectedHospitals();
          hospital = selectedHospital[0];
          profile.hospitals = [hospital];
          profile.principalHospitalId = hospital;
        }
      } else {
        posthog.capture('Edit_Profile', { page_source: 'Individual_Calendar' });
        profile.hospitals = this.profile?.hospitals?.map((h: any) => h._id);
        profile.principalHospitalId = profile?.hospitals?.[0];
      }

      profile.position = this.formGroup?.get('type')?.value;
      profile.residency = this.formGroup?.get('residency')?.value;
      profile.phoneNumber = this.formGroup?.get('phoneNumber')?.value;
      profile.firstName = this.formGroup?.get('firstName')?.value ? this.formGroup?.get('firstName')?.value : 'Dr';
      profile.lastName = this.formGroup?.get('lastName')?.value;
      const formationsGroupOfType = await this.userService.getFormationsGroupOfProfile({
        position: this.formGroup?.get('type')?.value,
      });
      if (formationsGroupOfType?.isUsingSeniorities) {
        profile.seniority = this.formGroup?.get('seniority')?.value;
      } else {
        profile.seniority = null;
      }
      profile.startingDate = transformedDate(this.formGroup?.get('startingDate')?.value);
      profile.profilePic = this.picture;
      profile.excludeInCalendarsConnector = this.formGroup.get('excludeInCalendarsConnector')?.value;
      // update pic in side nav if the change is not made from 'mon compte'
      if (profile._id === this.currentUser?.profile?._id) {
        this.storageService.profilePictureInLocalStorageSubject.next(this.picture);
      }
      const toAdd = this.matchingResultsToAdd();
      const toRemove = this.matchingResultsToRemove(profile._id);
      if (toAdd.length) {
        profile.matchingResultsToAdd = toAdd;
      }
      if (toRemove.length) {
        profile.matchingResultsToRemove = toRemove;
      }
      this.onProfileChange.emit({ profile: profile, showSkillsPopup: this.doesSelectedFormationHaveSkills });
    }
  }

  async initFormationsThatHaveSkills(): Promise<void> {
    try {
      this.formationsThatHaveSkills = await firstValueFrom(
        this.subscriptionManager.createRequest(
          this.paramedicalService.getFormationsThatHaveSkills(),
          'getFormationsThatHaveSkills',
          this.instanceId
        )
      );
    } catch (error: any) {
      console.error(error);
    }
  }

  deleteProfilePicture() {
    this.picture = '';
    this.imageSource = '';
  }

  matchingResultsToAdd() {
    return this.selectedOptions.map((opt: any) => opt._id);
  }

  matchingResultsToRemove(profileId: string) {
    const toRemove = [];
    const alreadyMatchedResultsId = this.options
      .filter((opt: any) => opt.matchedId === profileId)
      .map((opt: any) => opt._id);
    for (const alreadyMatchedResultId of alreadyMatchedResultsId) {
      if (!this.selectedOptions.find((opt: any) => opt._id === alreadyMatchedResultId)) {
        toRemove.push(alreadyMatchedResultId);
      }
    }
    return toRemove;
  }

  private initCalendarsDataPipelineOption(): void {
    this.doesHospitalHaveCalendarsDataPipelineOption =
      this.hospitalService.doesHospitalHaveCalendarsDataPipelineOption();

    if (!this.doesHospitalHaveCalendarsDataPipelineOption) {
      this.calendarsSoftwareName = null;
      return;
    }

    const selectedHospitalId = this.isMultiHospitals
      ? this.formGroup?.get('hospitals')?.value?._id
      : this.userService.getSelectedHospitals()[0];

    const hospital = this.userService
      .getCurrentUser()
      ?.profile?.hospitals?.find((h: any) => String(h._id) === String(selectedHospitalId));

    const option = hospital?.options?.find((e: any) => e.name === OPTIONS.CALENDARS_DATA_PIPELINE);

    this.calendarsSoftwareName = option?.softwareName ?? null;
  }

  goToEditSkills(): void {
    if (this.formGroup?.valid) {
      const profile: Profile = this.profile ? this.profile : {};
      profile.position = this.formGroup?.value?.type;
      profile.residency = this.formGroup?.value?.residency;
      profile.phoneNumber = this.formGroup?.value?.phoneNumber;
      profile.firstName = this.formGroup?.value?.firstName ? this.formGroup.value.firstName : 'Dr';
      profile.lastName = this.formGroup?.value?.lastName;
      profile.startingDate = transformedDate(this.formGroup?.value?.startingDate);
      profile.profilePic = this.picture;

      this.editSkills.emit(profile);
    }
  }

  onFileChange(event: any) {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      this._isExtensionAllowed = this.isExtensionFileAllowed(file);
      this._isFileSizeAllowed = this.isFileSizeAllowed(file);
      if (this._isFileSizeAllowed && this._isExtensionAllowed) {
        this.openDialog(event);
      } else {
        if (!this._isExtensionAllowed) {
          this.toastService.errorToast(ERROR_MSG_ON_EXTENSION);
        } else {
          this.toastService.errorToast(ERROR_MSG_ON_FILE_SIZE);
        }
      }
    }
  }

  openDialog(event: any): void {
    const dialogRef = this.dialog.open(CropperPopupComponent, {
      data: {
        imageChangedEvent: event,
      },
    });

    this.subscriptionManager.createRequest(dialogRef.afterClosed(), 'CropperPopupDialog', this.instanceId).subscribe({
      next: (result: any) => {
        if (result) {
          this.picture = result.base64;
          this.imageSource = result.base64;
        }
      },

      error: (error: any) => {
        this.errorService.handleError(error);
      },
    });
  }

  isFileSizeAllowed(file: any) {
    return file.size < FILE_SIZE_MAX;
  }

  isExtensionFileAllowed(file: any) {
    const extension = file.name.split('.')[1].toLowerCase();
    return ALLOWED_EXTENSIONS.includes(extension);
  }

  closeDialog(): void {
    this.onClose.emit();
  }

  async updateShowMatchingResults() {
    const position = this.formGroup?.get('type')?.value;
    const isProfileInNurses = await this.userService.isProfileInNurses({ position });
    const isProfileInIades = await this.userService.isProfileInIades({ position });
    const isProfileInAnesthesistes = await this.userService.isProfileInAnesthesistes({ position });
    const isProfileInSurgeons = await this.userService.isProfileInSurgeons({ position });
    const isProfileInCDB = await this.userService.isProfileInCDB({ position });

    if (!this.isMultiHospitals) {
      if (isProfileInCDB) {
        this.showMatchingResults = this.hospitalService.doesHospitalHaveCalendarsDataPipelineOption();
        return;
      } else if (isProfileInNurses) {
        this.showMatchingResults =
          this.hospitalService.doesHospitalHaveCalendarsDataPipelineOption() ||
          this.hospitalService.doesHospitalHaveProgramsDataPipelineOption();
        return;
      } else if (isProfileInAnesthesistes || isProfileInIades || isProfileInSurgeons) {
        this.showMatchingResults = this.hospitalService.doesHospitalHaveProgramsDataPipelineOption();
        return;
      }
      this.showMatchingResults = false;
      return;
    }
    const selectedHospitalId = this.formGroup?.get('hospitals')?.value._id;
    const currentHospital = this.currentUser?.profile?.hospitals?.find(
      (hospital: any) => String(hospital._id) === String(selectedHospitalId)
    );
    if (isProfileInCDB) {
      this.showMatchingResults =
        currentHospital?.options &&
        currentHospital?.options?.find((option: any) => option.name === OPTIONS.CALENDARS_DATA_PIPELINE);
      return;
    } else if (isProfileInNurses) {
      this.showMatchingResults =
        currentHospital?.options &&
        (currentHospital?.options?.find((option: any) => option.name === OPTIONS.CALENDARS_DATA_PIPELINE) ||
          currentHospital?.options?.find((option: any) => option.name === OPTIONS.PROGRAMS_DATA_PIPELINE));
      return;
    } else if (isProfileInAnesthesistes || isProfileInIades || isProfileInSurgeons) {
      this.showMatchingResults =
        currentHospital?.options &&
        currentHospital?.options?.find((option: any) => option.name === OPTIONS.PROGRAMS_DATA_PIPELINE);
      return;
    }
    this.showMatchingResults = false;
    return;
  }

  async updateSoftwareName() {
    if (!this.showMatchingResults) {
      this.softwareName = null;
      return;
    }
    const position = this.formGroup?.get('type')?.value;
    let selectedHospitalId: string;
    if (this.isMultiHospitals) {
      selectedHospitalId = this.formGroup?.get('hospitals')?.value._id;
    } else {
      selectedHospitalId = this.userService.getSelectedHospitals()[0];
    }
    const currentHospital = this.currentUser?.profile?.hospitals?.find(
      (hospital: any) => String(hospital._id) === String(selectedHospitalId)
    );
    if (!currentHospital?.options || !currentHospital?.options?.length) {
      this.softwareName = null;
      return;
    }

    const isProfileInNurses = await this.userService.isProfileInNurses({ position });
    const isProfileInIades = await this.userService.isProfileInIades({ position });
    const isProfileInAnesthesistes = await this.userService.isProfileInAnesthesistes({ position });
    const isProfileInSurgeons = await this.userService.isProfileInSurgeons({ position });
    const isProfileInCDB = await this.userService.isProfileInCDB({ position });

    if (isProfileInNurses || isProfileInIades) {
      const softwareNames = [];
      if (currentHospital?.options?.find((opt: any) => opt.name === OPTIONS.CALENDARS_DATA_PIPELINE)) {
        softwareNames.push(
          currentHospital?.options?.find((opt: any) => opt.name === OPTIONS.CALENDARS_DATA_PIPELINE)?.softwareName
        );
      }
      if (currentHospital?.options?.find((opt: any) => opt.name === OPTIONS.PROGRAMS_DATA_PIPELINE)) {
        softwareNames.push(
          currentHospital?.options?.find((opt: any) => opt.name === OPTIONS.PROGRAMS_DATA_PIPELINE)?.softwareName
        );
      }
      this.softwareName = softwareNames.length ? softwareNames.join(', ') : null;
      return;
    } else if (isProfileInAnesthesistes || isProfileInSurgeons) {
      if (currentHospital?.options?.find((opt: any) => opt.name === OPTIONS.PROGRAMS_DATA_PIPELINE)) {
        this.softwareName = currentHospital?.options?.find(
          (opt: any) => opt.name === OPTIONS.PROGRAMS_DATA_PIPELINE
        )?.softwareName;
        return;
      }
      this.softwareName = null;
      return;
    } else if (isProfileInCDB) {
      if (currentHospital?.options?.find((opt: any) => opt.name === OPTIONS.CALENDARS_DATA_PIPELINE)) {
        this.softwareName = currentHospital?.options?.find(
          (opt: any) => opt.name === OPTIONS.CALENDARS_DATA_PIPELINE
        )?.softwareName;
        return;
      }
      this.softwareName = null;
      return;
    }
    this.softwareName = null;
    return;
  }

  focusSearchElement(event: any) {
    if (event) {
      event.stopPropagation();
    }
    this.searchElement?.nativeElement.focus();
  }

  clearSearch(event: any) {
    event.stopPropagation();

    if (this.searchElement) {
      this.searchElement.nativeElement.value = '';
    }

    const selectedHospitalId = this.isMultiHospitals
      ? this.formGroup?.get('hospitals')?.value._id
      : this.userService.getSelectedHospitals()[0];

    const rawOptions = this.isMultiHospitals
      ? this.matchingResults.filter((mr: any) => String(mr.hospital) === String(selectedHospitalId))
      : this.matchingResults;

    this.options = this.enrichOptionsWithSoftwareLabel(rawOptions);

    if (this.selectFormControl) {
      if (!this.options || !this.options.length) {
        this.selectFormControl.disable();
      } else {
        this.selectFormControl.enable();
      }
    }

    this.sortOptions();
    this.matRefSelect();
  }

  onSearch(searchInput: string) {
    if (!this.matchingResults || !this.matchingResults.length) {
      return;
    }
    let selectedHospitalId: string;
    if (this.isMultiHospitals) {
      selectedHospitalId = this.formGroup?.get('hospitals')?.value._id;
    } else {
      selectedHospitalId = this.userService.getSelectedHospitals()[0];
    }
    const rawOptions = this.matchingResults.filter((matchingResult: any) => {
      return (
        matchingResult.name.toLowerCase().includes(searchInput.toLowerCase()) &&
        String(matchingResult.hospital) === String(selectedHospitalId)
      );
    });

    this.options = this.enrichOptionsWithSoftwareLabel(rawOptions);

    if (this.selectFormControl) {
      if (!this.options || !this.options.length) {
        this.selectFormControl.disable();
      } else {
        this.selectFormControl.enable();
      }
    }

    this.sortOptions();
    this.matRefSelect();
  }

  optionClick(opt: any) {
    const index = this.selectedOptions.map((opts: any) => opts._id).indexOf(opt._id);
    if (index > -1) {
      this.selectedOptions.splice(index, 1);
    } else {
      this.selectedOptions.push(opt);
    }
    this.sortOptions();
    this.matRefSelect();
  }

  onNameQuit() {
    const lastName = this.formGroup?.get('lastName')?.value?.toLowerCase();
    const firstName = this.formGroup?.get('firstName')?.value?.toLowerCase();

    this.options.forEach((option: any) => {
      const optionNames = option.name.split(' ');
      const firstNameOption = optionNames[0]?.toLowerCase();
      const lastNameOption = optionNames[1]?.toLowerCase();
      if (
        (lastName === lastNameOption && firstName === firstNameOption) ||
        (lastName === firstNameOption && firstName === lastNameOption)
      ) {
        this.optionClick(option);
      }
    });
  }

  matRefSelect() {
    const selectedOptionsId = this.selectedOptions.map((selectedOption: any) => selectedOption._id);
    this.selectFormControl.patchValue(this.selectedOptions);
    this.matRef?.options.forEach((option: MatOption) => {
      if (selectedOptionsId.includes(option.value._id)) {
        option.select();
      } else {
        option.deselect();
      }
    });
  }

  sortOptions() {
    const selectedOptionsId = this.selectedOptions.map((selectedOption: any) => selectedOption._id);
    this.options.sort((optA: any, optB: any) => {
      if (selectedOptionsId.includes(optA._id) && !selectedOptionsId.includes(optB._id)) {
        return -1;
      }
      if (!selectedOptionsId.includes(optA._id) && selectedOptionsId.includes(optB._id)) {
        return 1;
      }
      if (optA.name < optB.name) {
        return -1;
      }
      if (optA.name > optB.name) {
        return 1;
      }
      return 0;
    });
  }

  private getSelectedHospitalId(): string | null {
    if (this.isMultiHospitals) {
      return this.formGroup?.get('hospitals')?.value?._id ?? null;
    }
    return this.userService.getSelectedHospitals()?.[0] ?? null;
  }

  private getCurrentHospitalOptions(): any[] {
    const selectedHospitalId = this.getSelectedHospitalId();
    if (!selectedHospitalId) {
      return [];
    }

    const currentHospital = this.currentUser?.profile?.hospitals?.find(
      (h: any) => String(h._id) === String(selectedHospitalId)
    );

    return currentHospital?.options ?? [];
  }

  private computeSoftwareLabelFromPipelines(pipelines: string[]): string | null {
    if (!Array.isArray(pipelines) || !pipelines.length) {
      return null;
    }

    const hospitalOptions = this.getCurrentHospitalOptions();
    if (!hospitalOptions.length) {
      return null;
    }

    const optionNames: string[] = [];

    if (pipelines.includes('CALENDAR')) {
      optionNames.push(OPTIONS.CALENDARS_DATA_PIPELINE);
    }
    if (pipelines.includes('VACATION')) {
      optionNames.push(OPTIONS.VACATIONS_DATA_PIPELINE);
    }
    if (pipelines.some((p) => p === 'DA' || p === 'RP')) {
      optionNames.push(OPTIONS.PROGRAMS_DATA_PIPELINE);
    }

    const uniqueWanted = Array.from(new Set(optionNames));
    if (!uniqueWanted.length) {
      return null;
    }

    const softwares = uniqueWanted
      .map((optName) => hospitalOptions.find((o: any) => o.name === optName)?.softwareName)
      .filter((x: string | undefined) => !!x);

    const uniqueSoftwares = Array.from(new Set(softwares));
    return uniqueSoftwares.length ? uniqueSoftwares.join(', ') : null;
  }

  private enrichOptionsWithSoftwareLabel(options: any[]): any[] {
    return (options ?? []).map((mr: any) => ({
      ...mr,
      softwareLabel: this.computeSoftwareLabelFromPipelines(mr?.pipelines ?? []),
    }));
  }
}
